import { useState } from "react";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { FullScreenDialog, type DialogDismissReason } from "@/shared/components/ui/FullScreenDialog";
import { FormRow } from "./FormRow";
import { FormsList } from "./FormsList";
import { useForms } from "../hooks/formQueries";
import { useFormPreviewOverlay } from "../hooks/useFormPreviewOverlay";
import type { FormSummary } from "../types/formTypes";

interface FormPickerProps {
  open: boolean;
  selectedForms?: FormSummary[];
  onClose: (reason?: DialogDismissReason) => void;
  onSelected: (forms: FormSummary[]) => void;
}

export function FormPicker({ open, ...props }: FormPickerProps) {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();
  return open && user && businessId ? <FormPickerDraft key={`${user.id}:${businessId}`} {...props} /> : null;
}

function FormPickerDraft({ selectedForms = [], onClose, onSelected }: Omit<FormPickerProps, "open">) {
  const catalogue = useForms();
  const [draft, setDraft] = useState(selectedForms);
  const { openPreview } = useFormPreviewOverlay();
  const unavailable = catalogue.data ? draft.filter((form) => !catalogue.data.some((current) => current.id === form.id)) : [];
  const cannotApply = catalogue.isPending || !!catalogue.error || unavailable.length > 0;
  const remove = (id: string) => setDraft((current) => current.filter((form) => form.id !== id));
  return (
    <FullScreenDialog title="Select forms" onClose={onClose}
      footer={<div className="flex w-full flex-wrap items-center justify-between gap-3">
        <strong role="status" aria-live="polite" className="text-sm">{draft.length} selected</strong>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => onClose()}>Cancel</Button>
          <Button type="button" size="touch" disabled={cannotApply} onClick={() => {
            if (cannotApply) return;
            onSelected(draft);
            onClose();
          }}>Use selected forms</Button>
        </div>
      </div>}>
      <div className="space-y-4 px-4 py-4 sm:px-[30px]">
        {unavailable.length > 0 && <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">A selected form is no longer available. Remove it or cancel to keep your original draft.</p>
          {unavailable.map((form) => <FormRow key={form.id} form={form} onRemove={() => remove(form.id)} />)}
        </div>}
        <FormsList selectedForms={draft} onPreview={(form) => openPreview(form.id)} onToggle={(form) =>
          setDraft((current) => current.some((item) => item.id === form.id)
            ? current.filter((item) => item.id !== form.id) : [...current, form])} />
      </div>
    </FullScreenDialog>
  );
}
